import { type ActionReducerMapBuilder, isAnyOf } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';

import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { algoOrderApi } from '@/store/orders/algoOrders/api/index.ts';
import type {
  AlgoOrder,
  AlgoOrderRequest,
  CommonAlgoOrderProperties,
  FalconOrder,
  NightjarOrder,
  TwapOrder,
} from '@/store/orders/algoOrders/state.ts';
import {
  isError400WithPayload,
  type OrderById,
  type SubmitOrderResponse,
  type ValidateOrderResponse,
} from '@/store/orders/state.ts';

const mapProperties = (
  currentOrder: AlgoOrder,
  originalArgs: AlgoOrderRequest,
  payload?: ValidateOrderResponse,
): CommonAlgoOrderProperties => {
  const currentTime = Date.now();
  const clockOffset =
    payload?.backendTime !== null && payload?.backendTime !== 0 && typeof payload?.backendTime === 'number'
      ? payload?.backendTime - currentTime
      : undefined;

  const maturityDay = payload?.maturityDay;
  const expiryDay = payload?.expiryDay;

  return {
    status: currentOrder.properties.status,
    isReadyToSubmit: Boolean(payload?.isReadyToSubmit),
    mode: currentOrder.properties.mode,
    clockOffset,

    currencyPair: {
      currency1: originalArgs.currency1,
      currency2: originalArgs.currency2,
      tiering: currentOrder.properties.currencyPair?.tiering,
    },

    clientWay: originalArgs.way ?? currentOrder.properties.clientWay,

    notional: originalArgs.notional ?? currentOrder.properties.notional,
    notionalCurrencyIndex: originalArgs.notionalCurrencyIndex ?? currentOrder.properties.notionalCurrencyIndex,

    ndfFixingDate: originalArgs.isNdf ? (payload?.ndfFixingDate ?? currentOrder.properties.ndfFixingDate) : undefined,
    ndfFixingSource: originalArgs.isNdf
      ? (payload?.ndfFixingSource ?? currentOrder.properties.ndfFixingSource)
      : undefined,

    liquidityPool: originalArgs.liquidityPool ?? currentOrder.properties.liquidityPool,
    limitPrice:
      payload?.limitPrice ?? payload?.defaultPrice ?? originalArgs.limitPrice ?? currentOrder.properties.limitPrice,

    isNdf: originalArgs.isNdf,

    tenor: isDefined(maturityDay)
      ? format(new Date(maturityDay), 'yyyy-MM-dd')
      : (originalArgs.tenor ?? currentOrder.properties.tenor),
    expiryDate: isDefined(expiryDay)
      ? {
          ...currentOrder.properties.expiryDate,
          date: format(new Date(expiryDay), 'yyyy-MM-dd'),
          time: format(new Date(expiryDay), 'HH:mm:ss'),
        }
      : (originalArgs.expiryDate ?? currentOrder.properties.expiryDate),
    startDate: originalArgs.startDate ?? currentOrder.properties.startDate,

    notificationEmails: originalArgs.notificationEmails?.isOverridenEmails
      ? originalArgs.notificationEmails.emails
      : currentOrder.properties.notificationEmails,
  };
};

export const validateAlgoFulfilledExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(algoOrderApi.endpoints.validateAlgo.matchFulfilled, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];

    if (isNotDefined(currentOrder) || !isAlgoOrder(currentOrder, action.meta.arg.originalArgs.orderType)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(action.meta.arg.originalArgs.currency1) || isNotDefined(action.meta.arg.originalArgs.currency2)) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,

      type: action.meta.arg.originalArgs.orderType,
      clientId: currentOrder.clientId,

      properties: {
        ...currentOrder.properties,
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, action.payload),
        errors: {},
      },
    } as AlgoOrder;

    if (currentOrder.type === 'Twap+Order' && action.meta.arg.originalArgs.orderType === 'Twap+Order') {
      (state[tileId] as TwapOrder).properties.clippingMode =
        action.meta.arg.originalArgs.clippingMode ?? currentOrder.properties.clippingMode;
      (state[tileId] as TwapOrder).properties.spreadCapture =
        action.meta.arg.originalArgs.spreadCapture ?? currentOrder.properties.spreadCapture;
      (state[tileId] as TwapOrder).properties.clipSize =
        action.meta.arg.originalArgs.clipSize ?? currentOrder.properties.clipSize;
      (state[tileId] as TwapOrder).properties.randomize =
        action.meta.arg.originalArgs.randomize ?? currentOrder.properties.randomize;
    }

    if (currentOrder.type === 'FalconOrder' && action.meta.arg.originalArgs.orderType === 'FalconOrder') {
      (state[tileId] as FalconOrder).properties.speed =
        action.meta.arg.originalArgs.speed ?? currentOrder.properties.speed;
    }

    if (currentOrder.type === 'NightjarOrder' && action.meta.arg.originalArgs.orderType === 'NightjarOrder') {
      (state[tileId] as NightjarOrder).properties.speed =
        action.meta.arg.originalArgs.speed ?? currentOrder.properties.speed;
      (state[tileId] as NightjarOrder).properties.alphaSeeker =
        action.meta.arg.originalArgs.alphaSeeker ?? currentOrder.properties.alphaSeeker;
    }
  });

export const validateAlgoPendingExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(isAnyOf(algoOrderApi.endpoints.validateAlgo.matchPending), (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;
    const currentOrder = state[tileId];
    if (isNotDefined(currentOrder) || !isAlgoOrder(currentOrder, action.meta.arg.originalArgs.orderType)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,
      properties: {
        ...currentOrder.properties,

        clientWay: action.meta.arg.originalArgs.way ?? currentOrder.properties.clientWay,
        notional: action.meta.arg.originalArgs.notional,
        notionalCurrencyIndex: action.meta.arg.originalArgs.notionalCurrencyIndex,

        limitPrice: action.meta.arg.originalArgs.limitPrice ?? currentOrder.properties.limitPrice,
        isNdf: action.meta.arg.originalArgs.isNdf ?? currentOrder.properties.isNdf,
        liquidityPool: action.meta.arg.originalArgs.liquidityPool ?? currentOrder.properties.liquidityPool,
        tenor: action.meta.arg.originalArgs.tenor ?? currentOrder.properties.tenor,
        startDate: action.meta.arg.originalArgs.startDate ?? currentOrder.properties.startDate,
        expiryDate: action.meta.arg.originalArgs.expiryDate ?? currentOrder.properties.expiryDate,

        notificationEmails:
          action.meta.arg.originalArgs.notificationEmails?.emails ?? currentOrder.properties.notificationEmails,
      },
    } as AlgoOrder;

    if (currentOrder.type === 'Twap+Order' && action.meta.arg.originalArgs.orderType === 'Twap+Order') {
      (state[tileId] as TwapOrder).properties.clippingMode =
        action.meta.arg.originalArgs.clippingMode ?? currentOrder.properties.clippingMode;
      (state[tileId] as TwapOrder).properties.spreadCapture =
        action.meta.arg.originalArgs.spreadCapture ?? currentOrder.properties.spreadCapture;
      (state[tileId] as TwapOrder).properties.clipSize =
        action.meta.arg.originalArgs.clipSize ?? currentOrder.properties.clipSize;
      (state[tileId] as TwapOrder).properties.randomize =
        action.meta.arg.originalArgs.randomize ?? currentOrder.properties.randomize;
    }

    if (currentOrder.type === 'FalconOrder' && action.meta.arg.originalArgs.orderType === 'FalconOrder') {
      (state[tileId] as FalconOrder).properties.speed =
        action.meta.arg.originalArgs.speed ?? currentOrder.properties.speed;
    }

    if (currentOrder.type === 'NightjarOrder' && action.meta.arg.originalArgs.orderType === 'NightjarOrder') {
      (state[tileId] as NightjarOrder).properties.speed =
        action.meta.arg.originalArgs.speed ?? currentOrder.properties.speed;
      (state[tileId] as NightjarOrder).properties.alphaSeeker =
        action.meta.arg.originalArgs.alphaSeeker ?? currentOrder.properties.alphaSeeker;
    }
  });

export const submitAlgoRejectedExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(algoOrderApi.endpoints.submitAlgo.matchRejected, algoOrderApi.endpoints.updateAlgo.matchRejected),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentOrder = state[tileId];
      const payload = isSubmitOrderResponse(action.payload) ? action.payload : undefined;

      if (
        isNotDefined(currentOrder) ||
        isNotDefined(payload) ||
        !isAlgoOrder(currentOrder, action.meta.arg.originalArgs.orderType) ||
        payload.type !== 'failure'
      ) {
        return state;
      }

      const errorMessage = (() => {
        if (payload.errors.some((error) => error.errorDescription === 'timeout')) {
          return 'order.timeout';
        }
        if (payload.errors.some((error) => error.errorDescription === 'server.error')) {
          return 'order.serverError';
        }
        if (payload.errors.some((error) => error.errorDescription === 'order.unexpected')) {
          return 'order.unexpected';
        }
        return 'order.validationFailed';
      })();

      const areErrorsKnown = payload.errors.some((error) => error.field !== 'unknown');

      const errors = payload.errors.reduce(
        (acc, error) => {
          if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
            acc.notional = error;
          }

          if (error.field === 'base64Emails') {
            acc.notificationEmails = error;
          }

          if (error.field === 'dodging') {
            acc.randomize = error;
          }

          if (error.field === 'startDateUtc') {
            acc.startDate = error;
          }

          if (error.field === 'endDateUtc') {
            acc.expiryDate = error;
          }

          if (error.field === 'maturityDateString') {
            acc.tenor = error;
          }

          acc[error.field] = error;
          return acc;
        },
        {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
      );

      const actionType = action.meta.arg.endpointName;

      state[tileId] = {
        ...currentOrder,
        type: action.meta.arg.originalArgs.orderType,
        properties: {
          ...currentOrder.properties,
          status: areErrorsKnown
            ? { type: actionType === 'submitAlgo' ? 'creating' : 'submitted' }
            : { type: 'failed', errorMessage },
          errors: areErrorsKnown ? errors : {},
        },
      } as AlgoOrder;
    },
  );

export const validateAlgoRejectedExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(algoOrderApi.endpoints.validateAlgo.matchRejected, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];
    const payload = isError400WithPayload(action.payload) ? action.payload : undefined;

    if (isNotDefined(currentOrder) || !isAlgoOrder(currentOrder, action.meta.arg.originalArgs.orderType)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(payload)) {
      state[tileId] = {
        ...currentOrder,
        type: action.meta.arg.originalArgs.orderType,
        properties: {
          ...currentOrder.properties,
          status: { type: 'failed', errorMessage: 'order.unexpected' },
        },
      } as AlgoOrder;
      return;
    }

    const errors = isDefined(payload?.validationResponseDetailContent)
      ? payload.validationResponseDetailContent.reduce(
          (acc, error) => {
            if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
              acc.notional = error;
            }

            if (error.field === 'base64Emails') {
              acc.notificationEmails = error;
            }

            if (error.field === 'dodging') {
              acc.randomize = error;
            }

            if (error.field === 'startDateUtc') {
              acc.startDate = error;
            }

            if (error.field === 'endDateUtc') {
              acc.expiryDate = error;
            }

            if (error.field === 'maturityDateString') {
              acc.tenor = error;
            }

            acc[error.field] = error;
            return acc;
          },
          {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
        )
      : undefined;

    state[tileId] = {
      ...currentOrder,
      type: action.meta.arg.originalArgs.orderType,
      properties: {
        ...currentOrder.properties,
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, payload),
        errors,
      },
    } as AlgoOrder;
  });

export const submitAlgoFulfilledExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(algoOrderApi.endpoints.updateAlgo.matchFulfilled, algoOrderApi.endpoints.submitAlgo.matchFulfilled),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const currentOrder = state[tileId];
      logger.logInformation(`[ORDER] Tile with id ${tileId} submitted successfully.`);
      if (
        isNotDefined(currentOrder) ||
        !isAlgoOrder(currentOrder, action.meta.arg.originalArgs.orderType) ||
        action.payload.type === 'failure'
      ) {
        logger.logInformation(
          '[ORDER] submitted successfully but wrong Order not the expected type. {currentOrder_s} orderType {orderType_s}',
          JSON.stringify(currentOrder),
          action.meta.arg.originalArgs.orderType,
        );
        return state;
      }

      state[tileId] = {
        ...currentOrder,
        properties: {
          ...currentOrder.properties,
          status: {
            type: 'submitted',
            tradeId: action.payload.orderId,
          },
          isReadyToSubmit: false,
        },
      } as AlgoOrder;
    },
  );

export const submitAlgoPendingExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(algoOrderApi.endpoints.updateAlgo.matchPending, algoOrderApi.endpoints.submitAlgo.matchPending),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const currentOrder = state[tileId];
      if (isNotDefined(currentOrder) || !isAlgoOrder(currentOrder, action.meta.arg.originalArgs.orderType)) {
        return state;
      }

      state[tileId] = {
        ...currentOrder,
        properties: {
          ...currentOrder.properties,
          status: {
            type: 'pending',
          },
        },
      } as AlgoOrder;
    },
  );

function isSubmitOrderResponse(response: any): response is SubmitOrderResponse {
  return response && 'errors' in response;
}

export const isAlgoOrder = (order: unknown, orderType: string): order is AlgoOrder => {
  return (
    order !== null &&
    typeof order === 'object' &&
    (orderType === 'FalconOrder' || orderType === 'Twap+Order' || orderType === 'NightjarOrder')
  );
};
